/**
 * @file mgrchat.js
 * @description MGR Chat Records component for displaying customer chat data.
 * @component MGRChatRecords
 * @exports MGRChatRecords - Main component for displaying customer chat data.
 */

import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Container,
  Chip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { ExpandMore } from "@mui/icons-material";
import { getmgrchat } from "../services/api";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import { useTheme } from "@mui/material/styles";

const MGRChatRecords = () => {
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterResponses, setFilterResponses] = useState(2);
  const [filterLastResponse, setFilterLastResponse] = useState(10);

  useEffect(() => {
    const loadChatRecords = async () => {
      try {
        const fetchedChatRecords = await getmgrchat();
        if (Array.isArray(fetchedChatRecords)) {
          setCustomers(fetchedChatRecords);
        } else {
          console.error("Fetched data is not an array:", fetchedChatRecords);
        }
      } catch (error) {
        console.error("Error fetching chat records:", error);
      }
    };

    loadChatRecords();

    // Auto-refresh every 30 seconds
    //const interval = setInterval(loadChatRecords, 10000);
    const interval = setInterval(loadChatRecords, 1000);
    return () => clearInterval(interval);
  }, []);

  console.log("Customers Data:", customers);
  const filteredCustomers = customers.filter((customer) => {
    const matchesSearch = searchTerm
      ? customer.firstname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.lastname?.toLowerCase().includes(searchTerm.toLowerCase())
      : true;

    const responsesCount = customer.responsescount ?? 0;
    const lastResponseMins =
      customer.lastresponsemins ?? Number.MAX_SAFE_INTEGER;

    const matchesResponses = responsesCount >= filterResponses;
    const matchesLastResponse = lastResponseMins <= filterLastResponse;

    return (
      matchesSearch && matchesResponses && matchesLastResponse
    );
  });

  const theme = useTheme();

  return (
    <Container maxWidth={false} disableGutters>
      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size="grow">
          <TextField
            fullWidth
            label="Search Customers"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
          />
        </Grid>
        <Grid size="auto">
          <FormControl variant="outlined" size="small" sx={{ width: 260 }}>
            <InputLabel>Number of Responses</InputLabel>
            <Select
              value={filterResponses}
              onChange={(e) => setFilterResponses(Number(e.target.value))}
              label="Number of Responses"
            >
              <MenuItem value={2}>At least 1 client response</MenuItem>
              <MenuItem value={3}>At least 2 client responses</MenuItem>
              <MenuItem value={6}>At least 5 client responses</MenuItem>
              <MenuItem value={11}>At least 10 client responses</MenuItem>
              <MenuItem value={1}>All</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size="auto">
          <FormControl variant="outlined" sx={{ width: 260 }} size="small">
            <InputLabel>Last Client Response</InputLabel>
            <Select
              value={filterLastResponse}
              onChange={(e) => setFilterLastResponse(Number(e.target.value))}
              label="Last Client Response"
            >
              <MenuItem value={1}>In the last 1 minute</MenuItem>
              <MenuItem value={5}>In the last 5 minutes</MenuItem>
              <MenuItem value={10}>In the last 10 minutes</MenuItem>
              <MenuItem value={30}>In the last 30 minutes</MenuItem>
              <MenuItem value={120}>In the last 2 hrs</MenuItem>
              <MenuItem value={480}>In the last 8 hrs</MenuItem>
              <MenuItem value={14400}>In the last 10 days</MenuItem>
              <MenuItem value={1440000}>All</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Data Grid */}
      <Grid container spacing={2} mt={2}>
        {filteredCustomers.map((customer) => (
          <Grid size={{ xs: 12, sm: 6, xl: 4 }} key={customer.customerdataid}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography
                  variant="body"
                  sx={{
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {customer.lastname}, {customer.firstname}
                  <Chip
                    icon={<LocalPhoneIcon />}
                    label={customer.customeruniqueid.replace(
                      /(\d{3})(\d{3})(\d{4})/,
                      "$1-$2-$3"
                    )}
                    size="small"
                    sx={{ mx: 1 }}
                  />{" "}
                  <Chip
                    label={`ID: CRFN2025${customer.chatData?.[0]?.customerdataid}00`}
                    size="small"
                  />
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  height: "600px",
                  overflowY: "auto",
                  backgroundColor: "#FAFAFA",
                }}
              >
                {customer.chatData?.map((chat, chatIndex) => (
                  <Box className="ai-chatbox" key={chatIndex}>

                    <Box
                      className="ai-chatbox__message ai-chatbox__message--response"
                      sx={{
                        borderColor: chat.newcara
                          ? theme.palette.primary.main
                          : theme.palette.primary.main + "30",
                      }}
                    >
                      <Box className="ai-chatbox__message-content">
                        <Box component="span">{chat.cararesponse}</Box>
                      </Box>
                      <Box className="ai-chatbox__message-footer">
                        {chat.newcara && (
                          <Chip
                            label={chat.newcara}
                            size="small"
                            color="primary"
                            sx={{
                              height: "16px",
                              textTransform: "uppercase",
                              fontSize: "10px",
                              mr: 1,
                            }}
                          />
                        )}
                        <span>
                          <b>Cara</b>&nbsp;on&nbsp;
                          {new Date(chat.cararesponsetime).toLocaleString()}
                        </span>
                      </Box>
                    </Box>
                                        {/* user */}
                                        <Box
                      className="ai-chatbox__message ai-chatbox__message--input"
                      sx={{
                        borderColor: chat.newuser
                          ? theme.palette.primary.main
                          : theme.palette.primary.main + "60",
                      }}
                    >
                      <Box className="ai-chatbox__message-content">
                        {chat.userresponse}
                      </Box>
                      <Box className="ai-chatbox__message-footer">
                        {chat.newuser && (
                          <Chip
                            label={chat.newuser}
                            size="small"
                            color="primary"
                            sx={{
                              height: "16px",
                              textTransform: "uppercase",
                              fontSize: "10px",
                              mr: 1,
                            }}
                          />
                        )}
                        <span>
                          <b>User</b>&nbsp;on&nbsp;
                          {new Date(chat.userresponsetime).toLocaleString()}
                        </span>
                      </Box>
                    </Box>
                    {/* Cara */}
                    {/* {chatIndex < customer.chatData.length - 1 && (
                      <Divider sx={{ my: 1 }} />
                    )} */}
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default MGRChatRecords;
